import type { FC, ReactNode } from 'react'

import { useState } from 'react'
import { Heart } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useSavedHomes } from '@/hooks/use-saved-homes'

interface SaveButtonProps {
    listingId: string
    title: string
    variant?: 'overlay' | 'inline'
    className?: string
}

export const SaveButton: FC<SaveButtonProps> = ({ listingId, title, variant = 'overlay', className }): ReactNode => {
    const { isSaved, toggle } = useSavedHomes()
    const [popKey, setPopKey] = useState(0)
    const saved = isSaved(listingId)

    const handleClick = () => {
        toggle(listingId)
        setPopKey(key => key + 1)
    }

    if (variant === 'inline') {
        return (
            <button
                type='button'
                onClick={handleClick}
                aria-pressed={saved}
                aria-label={saved ? `Remove ${title} from saved homes` : `Save ${title}`}
                className={cn('flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold underline-offset-4 transition hover:bg-muted hover:underline', className)}
            >
                <Heart
                    key={popKey}
                    className={cn('size-4', saved && 'fill-primary text-primary', popKey > 0 && 'animate-heart-pop')}
                    aria-hidden='true'
                />
                {saved ? 'Saved' : 'Save'}
            </button>
        )
    }

    return (
        <button
            type='button'
            onClick={handleClick}
            aria-pressed={saved}
            aria-label={saved ? `Remove ${title} from saved homes` : `Save ${title}`}
            className={cn('flex size-9 items-center justify-center rounded-full transition hover:scale-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white', className)}
        >
            <Heart
                key={popKey}
                className={cn(
                    'size-6 stroke-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.45)]',
                    saved ? 'fill-primary' : 'fill-black/45',
                    popKey > 0 && 'animate-heart-pop'
                )}
                strokeWidth={2}
                aria-hidden='true'
            />
        </button>
    )
}
